'use client'

import { Badge, Cell, Image, Rating, Spacer } from '@vinted/web-ui'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { UserTitle } from '@marketplace-web/common-components/user-title-ui'
import { EMPTY_USER_IMAGE_NAME, useAsset } from '@marketplace-web/shared/assets'
import { profileClickEvent } from '@marketplace-web/item-page/item-page-data'

import { ItemPageUserInfoHeaderPluginModel } from './types'
import { MEMBER_PROFILE_URL } from '../../constants/routes'

type Props = {
  data: ItemPageUserInfoHeaderPluginModel
}

const calculateRating = (reputation: number): number => {
  return Math.round(reputation * 5 * 100) / 100
}

const ItemPageUserInfoHeaderPlugin = ({ data }: Props) => {
  const translate = useTranslate()
  const { track } = useTracking()
  const noPhotoAsset = useAsset('assets/no-photo')

  const {
    itemId,
    feedbackReputation,
    sellerId,
    business,
    businessAccount,
    feedbackCount,
    sellerLogin,
    sellerPhoto,
  } = data
  if (!sellerId) return null

  const rating = calculateRating(feedbackReputation)

  const handleOnClick = () => {
    if (!sellerId) return

    track(
      profileClickEvent({
        target: 'user_short_info_cell',
        ownerId: sellerId.toString(),
        itemId,
      }),
    )
  }

  return (
    <Cell
      prefix={
        <Image
          role="img"
          src={sellerPhoto?.url || noPhotoAsset(EMPTY_USER_IMAGE_NAME)}
          styling={Image.Styling.Circle}
          size={Image.Size.Large}
        />
      }
      title={
        <UserTitle
          badgeTitle={translate('business.title')}
          businessAccountName={businessAccount?.name}
          login={sellerLogin}
          isBusiness={business}
          isBody
          isBottomLoginHidden
          isBadgeHidden
        />
      }
      onClick={handleOnClick}
      url={MEMBER_PROFILE_URL(sellerId)}
      type={Cell.Type.Navigating}
      chevron
    >
      <Rating
        aria={{
          'aria-label': (rating && translate('user.a11y.rating', { rating, max_rating: 5 })) || '',
        }}
        value={rating}
        text={feedbackCount}
        size={Rating.Size.Small}
        emptyStateText={translate('user.feedback.no_reviews')}
      />
      {business && (
        <>
          <Spacer size={Spacer.Size.Small} />
          <Badge content={translate('business.title')} testId="seller-pro-badge" />
        </>
      )}
    </Cell>
  )
}

export default ItemPageUserInfoHeaderPlugin
